import { useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import i18n from 'i18n';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { setOperatorSessionSwitchStatus } from 'store/operator-session-switch.slice';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';

const ACTIVITY_LOG_PREFIX = 'ACTIVITY_LOG';

const SESSION_SWITCH_RECEIVED = 'OPERATOR_SESSION_SWITCH_RECEIVED';
const SESSION_SWITCH_SUCCESS = 'OPERATOR_SESSION_SWITCH_SUCCESS';
const SESSION_SWITCH_FAILED = 'OPERATOR_SESSION_SWITCH_FAILED';

const FAILURE_REASON_KEYS: Record<string, string> = {
  OPERATOR_NOT_FOUND: 'operator_session_switch_reason_operator_not_found',
  INVALID_PIN: 'operator_session_switch_reason_invalid_pin',
};

interface ActivityLogEntry {
  id: string;
  operatorId: string;
  operatorName: string;
  sessionId: string | null;
  action: string;
  timestamp: number;
  details: string | null;
  type: string;
  location: string | null;
  payload: unknown;
  syncTimestamp: number;
}

/**
 * Opens its own SSE connection to dispatch-monitor/live-dispatch-sse and
 * listens on the `xtab:{customerCode}:last:pubsub` channel for
 * OPERATOR_SESSION_SWITCH_{RECEIVED,SUCCESS,FAILED} acks sent by the tablet
 * in response to an operator/session-switch request. These arrive as an
 * `ACTIVITY_LOG|{json}` string — the "ACTIVITY_LOG" prefix is the log
 * channel, not the ack type; the ack type is the `action` field inside the
 * JSON payload, and the failure reason (OPERATOR_NOT_FOUND / INVALID_PIN)
 * is carried in `details`.
 *
 * Mounted globally (in App.tsx) so acks are picked up and toasted regardless
 * of which screen the dispatcher is on after triggering a login.
 */
export function useOperatorSessionSwitchSSE() {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const customerCode = useAppSelector((s) => s.persistedReducer.customer.code);
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const esRef = useRef<InstanceType<typeof EventSourcePolyfill> | null>(null);

  useEffect(() => {
    if (!accessToken || !customerId || !customerCode) return;

    const url = dispatchMonitorApi.liveDispatchSSEUrl(customerId);
    const channel = `xtab:${customerCode}:last:pubsub`;

    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    esRef.current = es;

    es.onmessage = function (e: any) {
      try {
        const outer = JSON.parse(e.data as string) as {
          channel?: string;
          message?: string | Record<string, any>;
          sse_heartbeat?: boolean;
        };

        if (outer?.sse_heartbeat) return;
        if (outer?.channel !== channel) return;
        if (typeof outer.message !== 'string') return;

        const separatorIndex = outer.message.indexOf('|');
        if (separatorIndex === -1) return;

        const prefix = outer.message.slice(0, separatorIndex);
        if (prefix !== ACTIVITY_LOG_PREFIX) return;

        const entry = JSON.parse(
          outer.message.slice(separatorIndex + 1)
        ) as ActivityLogEntry;

        if (
          entry.action !== SESSION_SWITCH_RECEIVED &&
          entry.action !== SESSION_SWITCH_SUCCESS &&
          entry.action !== SESSION_SWITCH_FAILED
        ) {
          return;
        }
        if (!entry.operatorId) return;

        const status =
          entry.action === SESSION_SWITCH_RECEIVED
            ? 'RECEIVED'
            : entry.action === SESSION_SWITCH_SUCCESS
            ? 'SUCCESS'
            : 'FAILED';

        dispatch(
          setOperatorSessionSwitchStatus({
            operatorId: entry.operatorId,
            status,
            sessionId: entry.sessionId ?? undefined,
            reason:
              status === 'FAILED' ? entry.details ?? undefined : undefined,
          })
        );

        if (status === 'SUCCESS') {
          dispatch(
            showNotification({
              message: i18n.t('operator_session_switch_toast_success'),
              type: 'success',
            })
          );
        } else if (status === 'FAILED') {
          const reasonKey = entry.details
            ? FAILURE_REASON_KEYS[entry.details]
            : undefined;
          const reasonText = reasonKey
            ? i18n.t(reasonKey)
            : i18n.t('operator_session_switch_reason_unknown');
          dispatch(
            showNotification({
              message: i18n.t('operator_session_switch_toast_failed', {
                reason: reasonText,
              }),
              type: 'error',
            })
          );
        }
      } catch (err) {
        console.error('[operator-session-switch-sse] parse error:', err);
      }
    };

    es.onerror = () => {
      // EventSourcePolyfill auto-reconnects — no action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [accessToken, customerId, customerCode, dispatchMonitorApi, dispatch]);
}

export default useOperatorSessionSwitchSSE;
